import { DAY_MAPPING_REVERSE } from '../constants/tabConfig';
import { ZoneDistributionUpdate, HistoricalHoursUpdate, DailyPercentageUpdate, DailyActualUpdate } from '../types';

export const extractZoneDistributionUpdates = (zoneTableData: any[], isActual: boolean = false): any[] => {
  const updates: any[] = [];

  zoneTableData.forEach((zone: any) => {
    const kcphOverride = zone.kcph?.override 
      ? parseFloat(zone.kcph.override) || 0 
      : parseFloat(zone.kcph?.default) || 0;

    Object.entries(zone.percentages).forEach(([day, values]: [string, any]) => {
      const dayNum = DAY_MAPPING_REVERSE[day as keyof typeof DAY_MAPPING_REVERSE];
      if (dayNum) {
        const valueToUse = values.override || values.default;

        if (isActual) {
          // For actual data, the value represents cases - send as overrideCases
          const overrideCases = parseFloat(valueToUse.toString().replace(/[^\d.-]/g, '')) || 0;
          
          updates.push({
            vpZone: zone.id.toUpperCase(),
            dayNum: dayNum,
            overrideCases: overrideCases, // API expects overrideCases for actual volume
            overrideKcph: kcphOverride,
          });
        } else {
          // For forecast data, send as overridePct
          const pctValue = parseFloat(valueToUse.toString().replace('%', '')) || 0;
          const overridePct = Number((pctValue / 100).toFixed(4));
          
          updates.push({
            vpZone: zone.id.toUpperCase(),
            dayNum: dayNum,
            overridePct: overridePct, // API expects overridePct for forecast
            overrideKcph: kcphOverride,
          });
        }
      }
    });
  });

  return updates;
};

export const extractHistoricalHoursUpdates = (historicalHoursTableData: any[]): HistoricalHoursUpdate[] => {
  const updates: HistoricalHoursUpdate[] = [];

  // For Global Save: Send ALL zones, ALL days with their current fixed_hours values
  // This includes both user-modified values and original display values (historical_hours copied to fixed_hours)
  historicalHoursTableData.forEach((zone: any) => {
    Object.entries(zone.fixedHours).forEach(([day, value]: [string, any]) => {
      const dayNum = DAY_MAPPING_REVERSE[day as keyof typeof DAY_MAPPING_REVERSE];
      if (dayNum) {
        // Always include the value, even if it's 0 or empty
        const fixedHours = parseFloat(value?.toString() || '0');
        updates.push({
          vpZone: zone.zone,
          dayNum: dayNum,
          fixedHours: isNaN(fixedHours) ? 0 : fixedHours
        });
      }
    });
  });

  return updates;
};

export const extractDailyPercentagesFromTableData = (tableData: any[]): DailyPercentageUpdate[] => {
  if (!tableData || !Array.isArray(tableData) || tableData.length === 0) return [];

  const row = tableData[0];
  if (!row || !row.percentages) return [];

  const dayMapping = {
    sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7
  };

  const result = [];
  for (const [dayKey, dayNum] of Object.entries(dayMapping)) {
    const dayData = row.percentages[dayKey];
    const percentageValue = dayData?.override ? parseFloat(dayData.override.replace('%', '')) / 100 : 0;
    
    result.push({
      dayNum: dayNum,
      override_daily_pct: percentageValue
    });
  }

  return result;
};

export const extractDailyActualDataFromTableData = (tableData: any[]): DailyActualUpdate[] => {
  if (!tableData || !Array.isArray(tableData) || tableData.length === 0) return [];

  const row = tableData[0];
  if (!row || !row.percentages) return [];

  const dayMapping = {
    sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7
  };

  const result = [];
  for (const [dayKey, dayNum] of Object.entries(dayMapping)) {
    const dayData = row.percentages[dayKey];
    const casesValue = dayData?.override ? parseFloat(dayData.override.replace('%', '')) : 0;
    const kcphValue = 120; // Default KCPH value
    
    result.push({
      dayNum: dayNum,
      override_daily_cases: casesValue,
      override_kcph: kcphValue
    });
  }

  return result;
};
